import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { FiCheckCircle, FiX, FiZap } from "react-icons/fi";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";

const STORAGE_KEY_PREFIX = "teamcast-release-notes-seen";

export default function ReleaseNotesDialog() {
    const { user } = useAuth();
    const [releaseNotes, setReleaseNotes] = useState(null);
    const [showDialog, setShowDialog] = useState(false);
    const releaseId = releaseNotes?.releaseId || releaseNotes?.updatedAt?.seconds || "current";
    const storageKey = user?.uid || user?.email
        ? `${STORAGE_KEY_PREFIX}:${releaseId}:${user.uid || user.email}`
        : `${STORAGE_KEY_PREFIX}:${releaseId}`;

    useEffect(() => {
        const unsubscribe = onSnapshot(
            doc(db, "siteConfig", "releaseNotes"),
            (snap) => {
                if (!snap.exists()) {
                    setReleaseNotes(null);
                    return;
                }

                const data = snap.data();
                const notes = Array.isArray(data.notes)
                    ? data.notes.map((note) => String(note).trim()).filter(Boolean)
                    : [];

                if (!data.enabled || notes.length === 0) {
                    setReleaseNotes(null);
                    return;
                }

                setReleaseNotes({
                    releaseId: String(data.releaseId || "current"),
                    title: data.title || "What's new",
                    description: data.description || "Here are the latest changes available in this version.",
                    notes,
                    updatedAt: data.updatedAt,
                });
            },
            (error) => {
                console.error("Failed to load release notes:", error);
                setReleaseNotes(null);
            }
        );

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!user || !releaseNotes) {
            setShowDialog(false);
            return;
        }

        try {
            setShowDialog(localStorage.getItem(storageKey) !== "true");
        } catch (error) {
            console.error("Failed to read release note state:", error);
            setShowDialog(true);
        }
    }, [releaseNotes, storageKey, user]);

    const handleClose = () => {
        try {
            localStorage.setItem(storageKey, "true");
        } catch (error) {
            console.error("Failed to save release note state:", error);
        }
        setShowDialog(false);
    };

    if (!showDialog || !releaseNotes) return null;

    return ReactDOM.createPortal(
        <Overlay role="presentation" onClick={handleClose}>
            <Dialog role="dialog" aria-modal="true" aria-labelledby="release-notes-title" onClick={(event) => event.stopPropagation()}>
                <CloseButton type="button" aria-label="Close update notes" onClick={handleClose}>
                    <FiX size={20} />
                </CloseButton>

                <IconCircle>
                    <FiZap size={26} />
                </IconCircle>

                <Eyebrow>TeamCAST update</Eyebrow>
                <Title id="release-notes-title">{releaseNotes.title}</Title>
                <Description>{releaseNotes.description}</Description>

                <NotesList>
                    {releaseNotes.notes.map((note) => (
                        <NoteItem key={note}>
                            <FiCheckCircle size={18} />
                            <span>{note}</span>
                        </NoteItem>
                    ))}
                </NotesList>

                <PrimaryButton type="button" onClick={handleClose}>
                    Got it
                </PrimaryButton>
            </Dialog>
        </Overlay>,
        document.body
    );
}

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(15, 23, 42, 0.46);
    backdrop-filter: blur(4px);
`;

const Dialog = styled.div`
    position: relative;
    width: min(92vw, 440px);
    padding: 28px;
    border-radius: 8px;
    background: #ffffff;
    color: #111827;
    text-align: left;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
`;

const CloseButton = styled.button`
    position: absolute;
    top: 14px;
    right: 14px;
    width: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 8px;
    background: #f3f4f6;
    color: #4b5563;
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;

    &:hover {
        background: #e5e7eb;
        color: #111827;
    }
`;

const IconCircle = styled.div`
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: #ecfeff;
    color: #0891b2;
    margin-bottom: 18px;
`;

const Eyebrow = styled.p`
    margin: 0 0 6px;
    color: #0f766e;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
`;

const Title = styled.h2`
    margin: 0;
    color: #111827;
    font-size: 1.55rem;
    line-height: 1.2;
    letter-spacing: 0;
`;

const Description = styled.p`
    margin: 10px 0 20px;
    color: #4b5563;
    font-size: 0.96rem;
    line-height: 1.55;
`;

const NotesList = styled.ul`
    display: grid;
    gap: 12px;
    margin: 0 0 24px;
    padding: 0;
    list-style: none;
`;

const NoteItem = styled.li`
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr);
    gap: 10px;
    align-items: start;
    color: #374151;
    font-size: 0.94rem;
    line-height: 1.45;

    svg {
        margin-top: 2px;
        color: #16a34a;
        flex: 0 0 auto;
    }
`;

const PrimaryButton = styled.button`
    width: 100%;
    min-height: 44px;
    border: none;
    border-radius: 8px;
    background: #2563eb;
    color: #ffffff;
    font-size: 0.96rem;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.2s;

    &:hover {
        background: #1d4ed8;
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }
`;
